import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const res = await apiService.login(
        credentials.account_no,
        credentials.username,
        credentials.password
      );
      return {
        user: res.user,
        store: res.store,
        expiresAt: res.expiresAt,
      };
    } catch (error) {
      return rejectWithValue(error.body?.error || error.message || 'Login failed');
    }
  }
);

try {
  localStorage.removeItem('authUser');
  localStorage.removeItem('authSession');
} catch (_) {}

let persistedUser = null;
let isSessionValid = false;
try {
  const raw = sessionStorage.getItem('authUser');
  const sessionData = sessionStorage.getItem('authSession');
  if (raw && sessionData) {
    const user = JSON.parse(raw);
    const session = JSON.parse(sessionData);
    const now = Date.now();
    const sessionExpiry = session.expiresAt || 0;
    if (now < sessionExpiry && apiService.getToken()) {
      persistedUser = user;
      isSessionValid = true;
    } else {
      sessionStorage.removeItem('authUser');
      sessionStorage.removeItem('authSession');
      apiService.setToken(null);
    }
  }
} catch {
  persistedUser = null;
  isSessionValid = false;
  try {
    sessionStorage.removeItem('authUser');
    sessionStorage.removeItem('authSession');
  } catch (_) {}
  apiService.setToken(null);
}

let sessionTimeoutId = null;

export const startSessionTimeout = () => (dispatch) => {
  if (sessionTimeoutId) clearTimeout(sessionTimeoutId);
  sessionTimeoutId = setTimeout(() => {
    dispatch(logout());
  }, 60 * 60 * 1000);
};

export const resetSessionTimeout = () => (dispatch) => {
  dispatch(startSessionTimeout());
};

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: persistedUser,
    isAuthenticated: isSessionValid,
    loading: false,
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      if (sessionTimeoutId) {
        clearTimeout(sessionTimeoutId);
        sessionTimeoutId = null;
      }
      apiService.logout();
      try {
        sessionStorage.removeItem('authUser');
        sessionStorage.removeItem('authSession');
      } catch (_) {}
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        try {
          sessionStorage.setItem('authUser', JSON.stringify(action.payload.user));
          const expiresAt = action.payload.expiresAt || Date.now() + 12 * 60 * 60 * 1000;
          sessionStorage.setItem('authSession', JSON.stringify({
            expiresAt,
            loginTime: Date.now(),
          }));
        } catch (_) {}
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        if (!state.user) state.isAuthenticated = false;
        state.user = null;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
